import { ReactNode, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuthStore } from "@/store/authStore";
import { Spinner } from "./Spinner";
import { useInactivityTimeout } from "@/hooks/useInactivityTimeout";

interface RequireAuthProps {
  children: ReactNode;
}

/** How long the whole browser may sit idle before the session ends.
 *
 *  24 hours (MPPI-00878). Named rather than inlined so the next person
 *  changing it finds the reasoning above the call site. */
const SESSION_IDLE_MS = 24 * 60 * 60 * 1000;

export function RequireAuth({ children }: RequireAuthProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const isLoading = useAuthStore((s) => s.isLoading);
  const initSession = useAuthStore((s) => s.initSession);
  const logout = useAuthStore((s) => s.logout);

  // 24 hours, raised from 1 hour on the operator's instruction 2026-10-20
  // (MPPI-00878). The 1-hour window was the thing that actually ended a
  // working day's session: Cognito's access token is also an hour, but it
  // refreshes itself ~5 minutes before expiry and the refresh token lasts
  // 30 days, so the token was never the constraint — this timer was.
  //
  // It is measured across EVERY TAB (MPPI-00846), so the 24 hours is 24
  // hours of the whole browser being untouched, not of one tab.
  //
  // **This is a deliberate weakening of an admin console's idle lock** and
  // it is recorded as such: a signed-in unattended machine now stays signed
  // in all day. It is the operator's call and a one-line reversal.
  useInactivityTimeout(SESSION_IDLE_MS, () => {
    logout();
    // Say WHY, or the person lands on a bare form and reads it as a fault
    // (MPPI-00876).
    navigate("/login?reason=inactive", { replace: true });
  });

  useEffect(() => {
    initSession();
  }, [initSession]);

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate(
        `/login?returnUrl=${encodeURIComponent(location.pathname)}&reason=expired`,
        { replace: true },
      );
    }
  }, [isLoading, isAuthenticated, navigate, location.pathname]);

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return <>{children}</>;
}
